import * as React from "react";
import ButtonTertiary from "../../../hiberUiKit/src/atoms/Buttons/ButtonTertiary";
import {MerchantPreferredBy} from "../../../model/job/BillOfMaterialsPreviewDetails";
import MerchantPreferences from "./MerchantPreferences";
import Routes from "../../../navigation/route/Route";
import {RouteComponentProps, RouteProps, withRouter} from "react-router";
import {Icon} from "../../../hiberUiKit/src/atoms/Icon/Icon";
import IconSmall from "../../../hiberUiKit/src/atoms/Icon/IconSmall";
import Job from "../../../model/job/Job";
import {none, Option, some} from "ts-option";
import {Bom} from "../../../model/job/Bom";
import {BillOfMaterialsPreview} from "../../../model/job/BillOfMaterialsPreview";
import JobProfit, {BoilerPriceStatus} from "../../../model/job/JobProfit";

interface ComponentProps {
	/**
	 * Job object
	 */
	job: Job;
	/**
	 * Job profits - bill of materials list
	 */
	jobProfits: BillOfMaterialsPreview[];
	/**
	 * Bom
	 */
	bomDetails: Option<Bom>
}

type Props = ComponentProps & RouteComponentProps<RouteProps>;

class JobBillOfMaterialsProfits extends React.PureComponent<Props> {

	render() {
		const jobProfits: BillOfMaterialsPreview[] = this.props.bomDetails
			.map(bom => {
				let newProfits = this.props.jobProfits
					.map(jp => (jp.bomId.orNull === bom.id || jp.merchant.id === bom.merchantId) ? jp.fillFromBom(bom) : jp)
				const profitsWithBoilerPrice: BillOfMaterialsPreview[] = newProfits.filter(
					i => i.profit.boilerPrice.status == BoilerPriceStatus.KNOWN && i.profit.boilerPrice.boilerPrice.isDefined);
				if (profitsWithBoilerPrice.length) {
					const minBoilerPrice = profitsWithBoilerPrice.reduce(
						(min, item) => item.profit.boilerPrice.boilerPrice.get.cents < min ?
							item.profit.boilerPrice.boilerPrice.get.cents : min,
						profitsWithBoilerPrice[0].profit.boilerPrice.boilerPrice.get.cents)

					newProfits = newProfits.map(
						jp => jp.profit.boilerPrice.boilerPrice.map(p => p.cents == minBoilerPrice).getOrElse(false) ?
							jp.copy({
								merchant: jp.merchant.copy({
									preferredBy: jp.merchant.preferredBy.flatMap(
										pb => {
											const isBoilerPreferred = pb.some(i => i == MerchantPreferredBy.BOILER)
											if (isBoilerPreferred) {
												return jp.merchant.preferredBy
											}
											else {
												return some([...pb, MerchantPreferredBy.BOILER])
											}
										})
								})
							})
							: jp.copy({
								merchant: jp.merchant.copy({
									preferredBy: jp.merchant.preferredBy.flatMap(pb => {
										const result = pb.filter(i => i != MerchantPreferredBy.BOILER);
										if (result.length) {
											return some(result)
										}
										else {
											return none
										}
									})
								})
							}))
				}
				return newProfits
			})
			.getOrElse(this.props.jobProfits)
		const isBomDefinedForJob = this.props.bomDetails.isDefined;
		return (
			<div className="c-common-info_row-container job-bill-of-materials_profits">
				<div className="job-bill-of-materials_table-row-inner-container">
					<table className="c-common-details_table">
						<thead>
						<tr>
							<th className={'align-left'}>Merchant Name</th>
							<th/>
							<th>Boiler buy price</th>
							<th>Job profit</th>
							<th className={'no-padding'}>=</th>
							<th>Parts profit</th>
							<th className={'no-padding'}>+</th>
							<th>Margin</th>
							<th className={'no-padding'}>+</th>
							<th>Total rebate</th>
							<th>Order total</th>
							<th/>
						</tr>
						</thead>
						<tbody>
						{jobProfits.map((profit, index) => (
							<tr key={index}
								className={
									(profit.merchant.preferredBy.isDefined &&
									profit.merchant.preferredBy.get.indexOf(MerchantPreferredBy.HIBER) !== -1 ? 'green-row ' : '')
									+ (isBomDefinedForJob && !profit.bomId.isDefined ? 'row-without-bom' : '')}>
								<td className={'align-left'}>{profit.merchant.name}</td>
								<td>
									{isBomDefinedForJob && profit.bomId.isDefined &&
									<div className={'align-left'}>
										<div className={'circle-wrapper sm green'}>
											<IconSmall icon={Icon.CheckMark} variant={"white"}/>
										</div>
										<span>Bill of Materials</span>
									</div>}
									{profit.incomplete &&
									<div className={'align-left'}>
										<div className={'circle-wrapper sm red'}><i className={'fas fa-exclamation'}/></div>
										<span className={'has-error'}>Order list incomplete</span>
									</div>}
									{profit.merchant.preferredBy.map(preferredBy => (
										<div className={'align-left'}>
											<MerchantPreferences merchantPreferredBy={preferredBy}/>
										</div>
									)).orNull}
								</td>
								<td>
									{this.getBoilerPrice(profit.profit)}
								</td>
								<td>{profit.profit.sum.map(
									item => item.format()).orNull}</td>
								<td className={'no-padding'}/>
								<td className={'weight-normal'}>
									{profit.profit.parts.map(item => item.format()).orNull}
								</td>
								<td className={'no-padding'}/>
								<td className={'weight-normal'}>{profit.profit.margin.format()}</td>
								<td className={'no-padding'}/>
								<td className={'weight-normal'}>{profit.profit.rebate.format()}</td>
								<td>{profit.total.map(item => item.format()).orNull}</td>
								<td>
									<ButtonTertiary label={'View ›'}
													onClick={() => this.navigateToDetailedBillOfMaterials(profit)}/>
								</td>
							</tr>
						))}
						</tbody>
					</table>
				</div>
			</div>
		);
	}

	navigateToJobsList() {
		this.props.history.push(Routes.JobsList);
	}

	navigateToDetailedBillOfMaterials = (profit: BillOfMaterialsPreview) => {
		const path = profit.bomId
			.map(bomId => Routes.BillOfMaterialsDetails(this.props.job.referenceNumber, bomId))
			.getOrElse(Routes.BillOfMaterialsPreview(this.props.job.referenceNumber, profit.merchant.id));
		this.props.history.push(path);
	}

	getBoilerPrice = (profit: JobProfit): string => {
		switch (profit.boilerPrice.status) {
			case BoilerPriceStatus.NO_BOILER:
				return "(No boiler)"
			case BoilerPriceStatus.UNKNOWN:
				return "unknown"
			case BoilerPriceStatus.KNOWN:
				return profit.boilerPrice.boilerPrice.map(v => v.format()).orNull
			default:
				return null
		}
	}
}

export default withRouter<ComponentProps>(JobBillOfMaterialsProfits)
